import { Box, Stack } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { _postFundUploadRequest, _putFunds, FundFileUploadRequest } from '@/api/fund/mutations';
import { _getSingleFundOptions, FUND_QUERY_KEYS } from '@/api/fund/queries';
import RefreshRightSquareRedIcon from '@/assets/svg/ui-elements/refresh-square-red.svg';
import {
  Button,
  Dropzone,
  FileItem,
  FileItemFrame,
  Modal,
  Select,
  Separator,
  TextArea,
  theme,
  Typography,
} from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { mapToPayload } from '@/pages/setup-fund/utils/map-step-form-to-payload';
import { Fund } from '@/pages/setup-fund/utils/type';
import {
  FILE_PROPAGATION_WAIT_MS,
  FUND_DOCUMENT_CATEGORY_OPTIONS,
  FUND_DOCUMENT_NOTES_MAX_LENGTH,
  FUND_DOCUMENT_REPLACE_DENIED_MESSAGE,
  FundDocumentCategoryKey,
} from '@/shared/constants/fund-documents';
import { colors } from '@/shared/design-system/colors';
import { spacing } from '@/shared/design-system/spacing';
import { Permission, RequirePermissionInline, useCan } from '@/shared/modules/permissions';
import { useGlobalModalsSlice } from '@/stores/selectors';
import { sleep } from '@/utils/sleep';
import { uploadToS3 } from '@/utils/upload-to-s3';

export type ReplaceFundDocumentModalProps = {
  fundId: string;
  category: FundDocumentCategoryKey;
  currentFile: { id: string; fileName: string; size: number };
};

export const ReplaceFundDocumentModal = () => {
  const { open, handleClose, props } = useGlobalModal('REPLACE_FUND_DOCUMENT_MODAL');
  const canManageFund = useCan(Permission.FundManage);
  const queryClient = useQueryClient();
  const { handleOpen: openGlobalModal, handleClose: closeGlobalModal } = useGlobalModalsSlice();

  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [isWaitingForPropagation, setIsWaitingForPropagation] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setNotes('');
    setIsWaitingForPropagation(false);
  }, [open]);

  const { data: fund, isLoading: isFundLoading } = useQuery({
    ..._getSingleFundOptions(props?.fundId || ''),
    enabled: open && !!props?.fundId && canManageFund,
    staleTime: 0,
  });

  const { mutateAsync: createUploadRequest, isPending: isCreatingRequest } = useMutation({
    mutationFn: _postFundUploadRequest,
  });

  const { mutateAsync: updateFund, isPending: isUpdatingFund } = useMutation({
    mutationFn: _putFunds,
  });

  const isSubmitting = isCreatingRequest || isUpdatingFund || isWaitingForPropagation;
  const canUploadFile = !isSubmitting && !!props?.category;
  const canSubmit =
    !!file && !!props?.fundId && !!props?.currentFile?.id && !isSubmitting && !!fund;

  const onFilePicked = (f: File | null | undefined) => {
    if (!f) return;
    setFile(f);
  };

  const doReplace = useCallback(async () => {
    if (!props?.fundId || !props?.category || !props?.currentFile?.id) return;
    if (!file) return;
    if (!fund) return;

    const contentType = file.type || 'application/pdf';

    const uploadReq = await createUploadRequest({
      fundId: props.fundId,
      filename: file.name,
      contentType,
      contentEncoding: 'gzip',
      category: 'document',
      file,
    } satisfies FundFileUploadRequest);

    await uploadToS3({
      uploadUrl: uploadReq.uploadUrl,
      uploadHeaders: uploadReq.uploadHeaders as Record<string, string>,
      file,
      fileId: uploadReq.fileId,
      compress: true,
    });

    const payload = mapToPayload(fund as Fund);
    const category = props.category;
    const section = (
      payload as unknown as Record<string, { notes: string; documentIds: string[] }>
    )[category] ?? { notes: '', documentIds: [] };

    const oldId = props.currentFile.id;
    const nextIdsRaw = (section.documentIds || []).filter(Boolean);
    const hasOld = nextIdsRaw.includes(oldId);
    const replaced = hasOld
      ? nextIdsRaw.map((id) => (id === oldId ? uploadReq.fileId : id))
      : nextIdsRaw;
    const nextIds = hasOld ? replaced : Array.from(new Set([...replaced, uploadReq.fileId]));
    const nextNotes = notes.trim() ? notes : section.notes || '';

    await updateFund({
      id: props.fundId,
      body: {
        ...payload,
        [category]: { notes: nextNotes, documentIds: nextIds },
      },
    });

    setIsWaitingForPropagation(true);
    await sleep(FILE_PROPAGATION_WAIT_MS);

    await queryClient.invalidateQueries({
      queryKey: [FUND_QUERY_KEYS.funds, props.fundId],
      refetchType: 'all',
    });

    handleClose();
  }, [createUploadRequest, file, fund, handleClose, notes, props, queryClient, updateFund]);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title="Replace Document"
      showClose
      maxWidth={720}
    >
      <Box>
        <Separator style={{ marginBottom: theme.spacing[24] }} />
        <RequirePermissionInline
          permission={Permission.FundManage}
          deniedMessage={FUND_DOCUMENT_REPLACE_DENIED_MESSAGE}
        >
          <Stack gap="24px" flexDirection="column">
            <Select
              size="lg"
              disabled
              value={props?.category ?? null}
              options={
                FUND_DOCUMENT_CATEGORY_OPTIONS as unknown as { label: string; value: string }[]
              }
              placeholder="Select document category"
            />

            <Box>
              <Typography
                variant="body3"
                color={colors.text.secondary}
                style={{ marginBottom: spacing[8] }}
              >
                Current file (to be replaced):
              </Typography>
              <FileItem
                file={{
                  id: props?.currentFile?.id ?? 'current-document',
                  name: props?.currentFile?.fileName || '—',
                  size: props?.currentFile?.size,
                  contentType: 'application/pdf',
                  status: 'uploaded',
                }}
              />
            </Box>

            {!file ? (
              <Dropzone
                subject="document"
                accept=".pdf,application/pdf"
                acceptLabel="PDF"
                disabled={!canUploadFile}
                onFilesSelected={([picked]) => onFilePicked(picked)}
              />
            ) : (
              <Box>
                <Separator style={{ marginBottom: theme.spacing[24] }} />
                <Typography
                  variant="body3"
                  color={colors.text.secondary}
                  style={{ marginBottom: spacing[8] }}
                >
                  New File
                </Typography>
                <FileItemFrame>
                  <FileItem
                    file={{
                      id: 'new-document',
                      name: file.name,
                      size: file.size,
                      contentType: file.type,
                      status: 'uploaded',
                    }}
                    onRemove={() => setFile(null)}
                  />
                </FileItemFrame>
              </Box>
            )}

            <TextArea
              label="Notes and Explanations"
              rows={4}
              placeholder="Notes and Explanations"
              value={notes}
              maxLength={FUND_DOCUMENT_NOTES_MAX_LENGTH}
              onChange={(e) => setNotes(e.target.value)}
            />

            <Button
              size="lg"
              fullWidth
              disabled={!canSubmit || isFundLoading}
              loading={isSubmitting}
              onClick={() => {
                openGlobalModal('CONFIRM_ACTION_MODAL', {
                  modalTitle: 'Replace Document',
                  btnLabel: 'Replace Document',
                  icon: <RefreshRightSquareRedIcon />,
                  confirmationTitle: 'Are you sure you want to replace this document?',
                  confirmationDescription:
                    'This will require the Fund to be reviewed and approved again by Gamma Prime. Until approval, the Fund will be hidden and unavailable for investment.',
                  btnColor: 'red',
                  isLoading: false,
                  onConfirm: async () => {
                    closeGlobalModal('CONFIRM_ACTION_MODAL');
                    try {
                      await doReplace();
                    } finally {
                      setIsWaitingForPropagation(false);
                    }
                  },
                });
              }}
            >
              Replace
            </Button>
          </Stack>
        </RequirePermissionInline>
      </Box>
    </Modal>
  );
};
